<template>
  <b-modal id="refund-modal" title="Issue Refund" hide-footer @hide="close">
    <b-overlay :show="loading" no-wrap fixed />
    <error-alert :errors="errors" />
    <b-form class="reporting-modal" @submit.prevent="issueRefund">
      <b-row
        v-for="(item, orderItemId) in refundSummary.order_items"
        :key="orderItemId"
        class="mb-2"
      >
        <b-col cols="8">
          <b-form-group class="mt-2">
            <b-form-checkbox
              v-model="checkboxes[orderItemId]"
              @change="toggleItemRefund(orderItemId, item.unrefunded)"
            >
              {{ item.name }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="4" class="text-right">
          <b-form-group
            class="mb-0"
            :description="`Unrefunded: $${getPrice(item.unrefunded)}`"
          >
            <currency-input
              v-model="orderItemRefunds[orderItemId]"
              :value-as-integer="true"
              :value-range="{
                min: 0,
                max: item.unrefunded / 100,
              }"
              class="form-control"
              placeholder="$0.00"
              :disabled="checkboxes[orderItemId]"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col cols="8">
          <b-form-group class="mt-2">
            <b-form-checkbox
              v-model="deliveryChecked"
              @change="
                toggleRefundCheckbox(
                  'deliveryRefund',
                  refundSummary.delivery_total.unrefunded
                )
              "
            >
              Delivery
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="4" class="text-right">
          <b-form-group
            class="mb-0"
            :description="`Unrefunded: $${getPrice(
              refundSummary.delivery_total.unrefunded
            )}`"
          >
            <currency-input
              v-model="deliveryRefund"
              :value-as-integer="true"
              :value-range="{
                min: 0,
                max: refundSummary.delivery_total.unrefunded / 100,
              }"
              :disabled="deliveryChecked"
              placeholder="$0.00"
              class="form-control"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col cols="8">
          <b-form-group class="mt-2">
            <b-form-checkbox
              v-model="tipChecked"
              @change="
                toggleRefundCheckbox('tipRefund', refundSummary.tip_total.unrefunded)
              "
            >
              Tip
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="4" class="text-right">
          <b-form-group
            class="mb-0"
            :description="`Unrefunded: $${getPrice(
              refundSummary.tip_total.unrefunded
            )}`"
          >
            <currency-input
              v-model="tipRefund"
              :value-as-integer="true"
              :value-range="{
                min: 0,
                max: refundSummary.tip_total.unrefunded / 100,
              }"
              :disabled="tipChecked"
              placeholder="$0.00"
              class="form-control"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col cols="8">
          <b-form-group class="mt-2">
            <b-form-checkbox
              v-model="serviceFeeChecked"
              @change="
                toggleRefundCheckbox(
                  'serviceFeeRefund',
                  refundSummary.service_fee_total.unrefunded
                )
              "
            >
              Service Fee
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="4" class="text-right">
          <b-form-group
            class="mb-0"
            :description="`Unrefunded: $${getPrice(
              refundSummary.service_fee_total.unrefunded
            )}`"
          >
            <currency-input
              v-model="serviceFeeRefund"
              :value-as-integer="true"
              :value-range="{
                min: 0,
                max: refundSummary.service_fee_total.unrefunded / 100,
              }"
              :disabled="serviceFeeChecked"
              placeholder="$0.00"
              class="form-control"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group class="mb-4" label="Notes" label-for="refund-notes">
        <b-form-textarea
          id="refund-notes"
          v-model="notes"
          placeholder="Enter your note..."
        />
      </b-form-group>
      <b-row v-if="orderCoupons.length">
        <table class="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Target</th>
              <th>Discount</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="orderCoupon in orderCoupons">
              <tr v-if="orderCoupon.targets.length <= 1" :key="orderCoupon.id">
                <td>{{ orderCoupon.code }}</td>
                <td>{{ orderCoupon.type }}</td>
                <td>
                  <template v-if="orderCoupon.type === 'percent'">
                    {{ orderCoupon.amount }}% Off
                  </template>
                  <template v-else>
                    {{ (orderCoupon.amount / 100) | currency }} Off
                  </template>
                </td>
                <td>{{ orderCoupon.target | capitalize }}</td>
                <td>{{ (orderCoupon.discount / 100) | currency }}</td>
              </tr>
              <tr v-for="target in orderCoupon.targets" v-else :key="target.id">
                <td>{{ orderCoupon.code }}</td>
                <td>{{ orderCoupon.type }}</td>
                <td>
                  <template v-if="orderCoupon.type === 'percent'">
                    {{ orderCoupon.amount }}% Off
                  </template>
                  <template v-else>
                    {{ (orderCoupon.amount / 100) | currency }} Off
                  </template>
                </td>
                <td>
                  {{ orderCoupon.target | capitalize }} ({{ target.target_id }})
                </td>
                <td>{{ (target.amount / 100) | currency }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </b-row>
      <b-row>
        <b-col>
          <button
            class="btn btn-link text-danger px-0"
            @click.prevent="issueRefund('full')"
          >
            Issue Full Refund<br />& Cancel Order
          </button>
        </b-col>
        <b-col class="text-right">
          <button class="btn btn-primary mt-3" type="submit">
            Issue Refund
          </button>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script>
import ResourceAPI from '../services/ResourceAPI';
import ErrorAlert from './ErrorAlert.vue';

export default {
  name: 'RefundModal',
  components: {
    ErrorAlert,
  },
  props: {
    networkOrderId: {
      type: Number,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      checkboxes: [],
      errors: {},
      loading: true,
      deliveryChecked: false,
      deliveryRefund: 0,
      tipChecked: false,
      tipRefund: 0,
      serviceFeeChecked: false,
      serviceFeeRefund: 0,
      orderItemRefunds: {},
      orderCoupons: [],
      notes: null,
      refundSummary: {
        order_items: {},
        delivery_total: {
          refunded: 0,
          unrefunded: 0,
          total: 0,
        },
        tip_total: {
          refunded: 0,
          unrefunded: 0,
          total: 0,
        },
        service_fee_total: {
          refunded: 0,
          unrefunded: 0,
          total: 0,
        },
      },
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$bvModal.show('refund-modal');
          this.loadNetworkOrder();
        } else {
          this.$bvModal.hide('refund-modal');
        }
      },
    },
  },
  methods: {
    getPrice(item) {
      return (item * 0.01).toFixed(2);
    },
    close(issued) {
      this.$emit('dismissed', issued);
      this.loading = false;
      this.errors = {};
      this.checkboxes = [];
      this.tipChecked = false;
      this.tipRefund = 0;
      this.deliveryChecked = false;
      this.deliveryRefund = 0;
      this.serviceFeeChecked = false;
      this.serviceFeeRefund = 0;
      this.orderCoupons = [];
      this.notes = null;
    },
    toggleItemRefund(id, value) {
      if (this.orderItemRefunds[id] === value) {
        this.orderItemRefunds[id] = 0;
      } else {
        this.orderItemRefunds[id] = value;
      }
    },
    toggleRefundCheckbox(field, max) {
      this[field] = this[field] === max ? 0 : max;
    },
    loadNetworkOrder() {
      this.loading = true;
      ResourceAPI.getRecord('network-orders', this.networkOrderId, {
        appends: 'refundSummary',
        with: 'orderCoupons',
      })
        .then((data) => {
          const orderItemIds = Object.keys(data.data.refundSummary.order_items);

          this.loading = false;
          this.refundSummary = data.data.refundSummary;
          this.orderCoupons = data.data.order_coupons || [];

          for (let i = 0; i < orderItemIds.length; i += 1) {
            this.$set(this.orderItemRefunds, orderItemIds[i], 0);
            this.$set(this.checkboxes, orderItemIds[i], false);
          }
        })
        .catch((error) => alert(error));
    },
    issueRefund(val) {
      let fullRefund = 0;

      this.loading = true;
      this.errors = {};

      if (val === 'full') {
        fullRefund = 1;
      }

      ResourceAPI.makePostRequest('network-order-refunds', {
        full: fullRefund,
        network_order_id: this.networkOrderId,
        delivery_total: this.deliveryRefund,
        tip_total: this.tipRefund,
        service_fee_total: this.serviceFeeRefund,
        order_items: this.orderItemRefunds,
        notes: this.notes,
      })
        .then((response) => {
          this.loading = false;

          if (response.data.success) {
            this.close(true);
          } else {
            this.errors = response.data.error;
          }
        })
        .catch((error) => alert(error));
    },
  },
};
</script>

